import React, { useState } from 'react';
import Accordion from '../../../../Accordion';
import {
  MapPin,
  Zap,
  TrendingUp,
  Square,
  Activity,
  DollarSign,
  Info,
  Database,
  Calculator,
  FileText
} from 'lucide-react';

/**
 * Documentation card for Renewable Potential tab (wrapped by parent ChartCard).
 */
const RenewablePotentialDocumentationAccordion = () => {
  const [activeKey, setActiveKey] = useState(null);

  return (
    <div className="mt-6 w-full min-w-0 overflow-hidden bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 md:px-6 lg:px-8 py-4 md:py-5 border-b border-gray-200">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900">
              Documentation
            </h3>
            <p className="text-sm md:text-base text-gray-600 mt-0.5 break-words">
              Data sources, processing methods, and methodology
            </p>
          </div>
        </div>
      </div>

      <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)} className="border-0">
          <Accordion.Item eventKey="0" className="border-0 border-b border-gray-100">
            <Accordion.Header className="px-4 md:px-6 lg:px-8 py-4 md:py-5 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3 w-full min-w-0">
                <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                  <Info className="h-4 w-4 text-blue-600" />
                </div>
                <div className="text-left min-w-0">
                  <h4 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 mb-0">Purpose</h4>
                  <p className="text-xs md:text-sm text-gray-500 mt-0.5 mb-0">Overview of the Renewable Potential tab</p>
                </div>
              </div>
            </Accordion.Header>
            <Accordion.Body className="px-4 md:px-6 lg:px-8 pb-4 md:pb-5">
              <div className="space-y-2">
                <p className="text-xs lg:text-sm text-gray-700 break-words">
                  Display technical potential and quality of solar and wind resources across the country
                </p>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="1" className="border-0 border-b border-gray-100">
            <Accordion.Header className="px-4 md:px-6 lg:px-8 py-4 md:py-5 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3 w-full min-w-0">
                <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                  <Database className="h-4 w-4 text-green-600" />
                </div>
                <div className="text-left min-w-0">
                  <h4 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 mb-0">
                    Visualizations & Sources
                  </h4>
                  <p className="text-xs md:text-sm text-gray-500 mt-0.5 mb-0">Data sources and processing methods</p>
                </div>
              </div>
            </Accordion.Header>
            <Accordion.Body className="px-4 md:px-6 lg:px-8 pb-4 md:pb-5">
              <div className="space-y-4">
                <div>
                  <h5 className="text-xs md:text-sm font-semibold text-gray-900 mb-2 break-words">
                    Solar Energy Potential & Wind Energy Potential (Maps + Summary Stats)
                  </h5>

                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <Database className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Data Sources</p>
                        <ul className="text-xs lg:text-sm text-gray-700 space-y-1 list-disc list-inside break-words">
                          <li>
                            <strong>REZoning</strong> (World Bank ESMAP + UCSB) - Grid cell zones, capacity estimates,
                            area, cost, onshore/offshore classification (wind)
                          </li>
                          <li><strong>Atlite</strong> - Capacity factor calculations from ERA5 weather data</li>
                        </ul>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <Calculator className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Processing</p>
                        <ul className="text-xs lg:text-sm text-gray-700 space-y-1 list-disc list-inside break-words">
                          <li>Grid cell shapes and initial potential zones from REZoning tool</li>
                          <li>Capacity factors (CF %) recalculated using Atlite weather data for accuracy</li>
                          <li>
                            Generation updated based on new CFs:{' '}
                            <code className="bg-gray-100 px-1 rounded">Generation = Capacity × CF × 8760 hours</code>
                          </li>
                          <li>Quality categories (Excellent/High/Good/Fair/Poor) assigned based on CF thresholds</li>
                        </ul>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <FileText className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Note</p>
                        <p className="text-xs lg:text-sm text-gray-700 break-words">
                          REZoning is a multi-criteria analysis tool developed by the World Bank&apos;s ESMAP program
                          in partnership with UC Santa Barbara. It identifies suitable zones for renewable development
                          using global geospatial datasets and industry-standard economic assumptions. Based on
                          LBNL/UCSB&apos;s MapRE platform.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="2" className="border-0">
            <Accordion.Header className="px-4 md:px-6 lg:px-8 py-4 md:py-5 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3 w-full min-w-0">
                <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                  <TrendingUp className="h-4 w-4 text-purple-600" />
                </div>
                <div className="text-left min-w-0">
                  <h4 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 mb-0">
                    Summary Statistics
                  </h4>
                  <p className="text-xs md:text-sm text-gray-500 mt-0.5 mb-0">Metrics displayed below each map</p>
                </div>
              </div>
            </Accordion.Header>
            <Accordion.Body className="px-4 md:px-6 lg:px-8 pb-4 md:pb-5">
              <div className="space-y-2">
                <p className="text-xs lg:text-sm text-gray-700 mb-3 break-words">Displayed below each map:</p>
                <ul className="text-xs lg:text-sm text-gray-700 space-y-2 list-none break-words">
                  <li className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <strong>Zones:</strong> Number of identified grid cells with potential
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <strong>Capacity:</strong> Total technical potential in MW (from REZoning)
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <TrendingUp className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <strong>Avg CF:</strong> Average capacity factor across all zones (from Atlite)
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <Square className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <strong>Area:</strong> Total area of potential zones in km²
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <Activity className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <strong>Generation:</strong> Annual generation potential in GWh (Capacity × Atlite CF × 8760)
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <DollarSign className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <strong>Cost:</strong> Levelized cost of energy (LCOE) in $/kW (from REZoning)
                    </div>
                  </li>
                </ul>
              </div>
            </Accordion.Body>
          </Accordion.Item>
      </Accordion>
    </div>
  );
};

export default RenewablePotentialDocumentationAccordion;
